import React, { useRef } from 'react';
import styled from 'styled-components';
import { muiChevronDown } from '@ally/metronome-icons';
import { useFloating, offset, flip, useListNavigation, useInteractions, useClick, useDismiss, FloatingFocusManager, autoUpdate, shift, } from '@floating-ui/react-dom-interactions';
import { v4 as uuidv4 } from 'uuid';
import { Icon } from '../Icon';
import { DropdownMenuItem } from './DropdownMenuItem';
import { MenuContext } from './Context';
const StyledMenu = styled.ul `
  ${({ theme: { colors } }) => `
    border-radius: 4px;
    border: 1px solid ${colors['gray-200']};
    background-color: ${colors.white};
    z-index: 1;

    list-style-type: none;
    margin: 0;
    padding: 0;

    box-shadow: 0 4px 8px -2px ${colors.black}16,
      0 10px 12px -4px ${colors.black}08
  `}
`;
const Trigger = styled.button `
  ${({ theme: { colors, font } }) => `
    border: none;
    background-color: transparent;
    display: flex;
    align-items: center;
    font-size: ${font.body.md.size};
    padding: 0;
    font-weight: bold;
    line-height: ${font.body.md.lineHeight};
    color: ${colors.bluesuedeshoes};
`}
`;
const StyledIcon = styled(Icon) `
  margin: 6px;
`;
export const DropdownMenuBase = ({ dropdownItems, title, placement = 'bottom-start', open, onOpenChange, activeIndex, onActiveIndexChange, selectedIndex, onSelectedIndexChange, selectionAsTitle = false, className, disableReturnFocus = false, ariaLabelTrigger, }) => {
    var _a;
    const menuItemsRef = useRef([]);
    const dropdownMenuId = `dropdown-menu-${uuidv4()}`;
    const ariaLabel = ariaLabelTrigger(open, title);
    const { x, y, reference, floating, strategy, context } = useFloating({
        open,
        onOpenChange,
        whileElementsMounted: autoUpdate,
        placement,
        middleware: [offset(5), flip({ padding: 8 }), shift()],
    });
    const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([
        useClick(context),
        useListNavigation(context, {
            listRef: menuItemsRef,
            activeIndex,
            selectedIndex,
            onNavigate: onActiveIndexChange,
        }),
        useDismiss(context, {
            escapeKey: true,
        }),
    ]);
    const options = dropdownItems.map((item, index) => (React.createElement(DropdownMenuItem, Object.assign({ allytmln: item.allytmln, active: index === activeIndex }, (item.ariaLabel ? { 'aria-label': item.ariaLabel } : {}), { key: item.text, index: index, selected: selectedIndex === index, onItemSelect: () => onSelectedIndexChange(index), menuItemsRef: menuItemsRef, onOpenChange: onOpenChange, getItemProps: getItemProps }, item))));
    const floatingProps = getFloatingProps({
        ref: floating,
        style: {
            position: strategy,
            top: y !== null && y !== void 0 ? y : '',
            left: x !== null && x !== void 0 ? x : '',
            overflow: 'auto',
        },
    });
    const customFloatingProps = Object.assign(Object.assign({}, floatingProps), { 
        // This code fixes an issue where pressing shift+tab on first
        // option would close menu but move focus backwards twice instead of once
        onKeyDown: (e) => {
            if (activeIndex === 0 && e.key === 'Tab' && e.shiftKey) {
                e.preventDefault();
                onOpenChange(false);
            }
            else {
                floatingProps.onKeyDown(e);
            }
        } });
    return (React.createElement(MenuContext.Provider, { value: {
            activeIndex,
            onActiveIndexChange,
            selectedIndex,
            onSelectedIndexChange,
            menuItemsRef,
            onOpenChange,
            getItemProps,
            dataRef: context.dataRef,
        } },
        React.createElement(Trigger, Object.assign({ type: "button" }, getReferenceProps({
            ref: reference,
            className: 'SelectButton',
        }), { "aria-label": ariaLabel, "aria-expanded": open, "aria-haspopup": "true", "aria-controls": dropdownMenuId, "data-testid": "test-dropdown-trigger", className: className }),
            React.createElement("span", { "data-testid": "test-dropdown-trigger-text" }, selectionAsTitle && selectedIndex != null
                ? (_a = dropdownItems === null || dropdownItems === void 0 ? void 0 : dropdownItems[selectedIndex].text) !== null && _a !== void 0 ? _a : title
                : title),
            React.createElement(StyledIcon, { icon: muiChevronDown, size: "xs", fill: "bluesuedeshoes", ariaHidden: true })),
        open && (React.createElement(FloatingFocusManager, { context: context, modal: false, returnFocus: !disableReturnFocus },
            React.createElement(StyledMenu, Object.assign({ id: dropdownMenuId, "data-testid": "test-dropdown-menu", role: "menu", "aria-label": title }, customFloatingProps), options)))));
};
//# sourceMappingURL=DropdownMenuBase.js.map